import React, { useState } from 'react';
import { Row, Col, Modal, Button, Collapse, Form } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import BettingHistoryTitle from './betting_history_title.js';
import BetMatchCard from './bet_match_card.js';
import { getBetSlipDisplayItems } from '../profile/bettingHistory/bettingHistoryDisplayUtils.js';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { postCashoutBetSlipReq } from 'stores/betSlip/services.js';
import GoBack from './go_back';
import SubTitleH6 from './subTitle_h6';
import TitleH6 from '../../components/general/title_h6.js';
import { gameDay, gameTime } from './../../toolkit/utils';
import { errorToaster, showToaster } from 'mixin/general';
import FetchButton from 'components/general/FetchButton';
import { useEffect } from 'react';
import { AWS_BUCKET_URL } from '../../toolkit/constants';
import { ConvertDisplayNumber } from '../../toolkit/NumberUtil';
import { EnumBetslipStatus, EnumMatchStatusName, EnumMarketStatus } from './../../toolkit/Enums';
import { getSingleBetSlipReq } from 'stores/auth/services.js';
import { addBetToBetSlip, removeAllBetSlip, setTotal } from 'stores/betSlip';
import { fetchFullEvent } from 'stores/match';
import useCurrency from 'toolkit/useHooks/useCurrency';

// import BetModal from 'components/general/bet_modal';

function BettingHistoryDetail() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { t } = useTranslation('translations', { useSuspense: false });
  const { code: currencyCode } = useCurrency();
  const userLimits = useSelector((state) => state.auth.userLimits);
  const events = useSelector((state) => state.match.events);
  const { slipId } = useParams();
  const [data, setData] = useState();
  const [showCashoutForm, setShowCashoutForm] = useState(false);
  const [showBetDetails, setShowBetDetails] = useState(false);
  const [isCashoutInvalid, setIsCashoutInvalid] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCashoutHandle, setIsCashoutHandle] = useState(false);
  const [isRebetting, setIsRebetting] = useState(false);
  const getBettingHistoryParams = () => ({
    betSlipsystemId: slipId
  });

  useEffect(() => {
    window.scrollTo(0, 0);
    getData();
  }, [isCashoutHandle]);

  const getData = async () => {
    const response = await getSingleBetSlipReq(getBettingHistoryParams());
    setData(response.data);
    if(response.data?.accountType === 5000){
      setCashout(response.data?.cashOut);
    }
  };

  const cashoutValue = Number(data?.cashOut?.replace(',', '')).toFixed(2);
  const [cashout, setCashout] = useState();
  const maxWinning = data?.maxWinning.replace(',', '');

  // Check if matches are available for re-betting
  const getAvailableMatchesForRebet = () => {
    if (!data?.matches) return [];
    
    return data.matches.filter(match => {
      // Check if match is live (status > NOT_STARTED and != ENDED)
      const isLive = match?.matchStatus > EnumMatchStatusName.NOT_STARTED && match?.matchStatus !== EnumMatchStatusName.ENDED;
      
      // Check if match is upcoming (NOT_STARTED and start date is in the future)
      const isUpcoming = match.matchStatus === EnumMatchStatusName.UNKNOWN || match?.matchStatus === EnumMatchStatusName.NOT_STARTED && 
        match?.startDate && new Date(match.startDate) > new Date();
      
      // Check if outcome is undecided yet (outcomeStatus === 2)
      const isOutcomeUndecided = match?.outcomeStatus === 2;
      
      return (isLive || isUpcoming) && isOutcomeUndecided;
    });
  };

  const availableMatchesForRebet = getAvailableMatchesForRebet();
  const canRebet = availableMatchesForRebet.length > 0;

  const handleRebet = async () => {
    if (!canRebet) return;

    setIsRebetting(true);
    try {
      // Clear existing betslip
      dispatch(removeAllBetSlip());
      
      // Set the same stake as the original bet
      dispatch(setTotal(Number(data?.totalStake) || 10));
      
             // Fetch current match data with markets for each available match
       const matchPromises = availableMatchesForRebet.map(async (match) => {
         try {
           // First check if the event is already in the store
           let fullEvent = events[match.eventId];
           
           // If not in store, fetch it
           if (!fullEvent) {
             const fullEventResponse = await dispatch(fetchFullEvent(match.eventId));
             fullEvent = fullEventResponse.payload;
           }
           
           if (fullEvent && fullEvent.markets) {
             // Find the market that matches the original bet
             const market = fullEvent.markets.find(m => m.stringId === match.marketId);
             if (market) {
               const outcome = market.outcomes?.find(o => o.id === match.outcomeId);
               if (outcome && outcome.odds > 0) {
                 dispatch(addBetToBetSlip({
                   providerId: match.eventId,
                   marketStringId: match.marketId,
                   marketId: market.id,
                   outcomeId: match.outcomeId,
                   outcomeName: match.outcomeName,
                   odds: outcome.odds,
                   match: fullEvent
                 }));
               }
             }
           }
         } catch (error) {
           console.error(`Error fetching match ${match.eventId}:`, error);
         }
       });
      
      // Wait for all match data to be fetched
      await Promise.all(matchPromises);
      
      // Navigate to betslip to show the new betslip
      navigate('/bet-slip');
    } catch (error) {
      console.error('Error in handleRebet:', error);
    } finally {
      setIsRebetting(false);
    }
  };

  const titles = (
    <div>
      <div className="sport_time_detail sport-title">
        <SubTitleH6 title={t('mybets')} />
      </div>
      <div className="sport_time_detail title2">
        <TitleH6 title={t('bettingSlipDetails')} />
      </div>
    </div>
  );

  const handleCashoutFormSubmit = async (e) => {
    try {
      e.preventDefault();
      setIsSubmitting(true);
      if (isCashoutInvalid || cashout === '') {
        setIsSubmitting(false);
        return;
      }
      const body = {
        systemSlipId: slipId,
        proposedCashOutAmount: cashoutValue,
        isFullCashOut: cashoutValue === cashout
      };
      if (!body.isFullCashOut) {
        body.cashOutAmount = Number(cashout);
      }
      const response = await postCashoutBetSlipReq(body);
      if (response.data && response.data.success) {
        showToaster(t('pages.bettingHistoryDetail.successMessageCashout'), 'success');
        navigate('/mybets');
      } else {
        if (
          response.data.messages[0].extraParam1 === null ||
          response.data.messages[0].extraParam1 === undefined ||
          response.data.messages[0].extraParam1 === ''
        ) {
          const matches = response.data.messages[0].text.match(/(\d+\.\d+)/g);
          if (matches) {
            const proposedValue = parseFloat(matches[0]); // 10.00
            const reportedValue = parseFloat(matches[1]); // 10.50
            showToaster(
              t('pages.bettingHistoryDetail.errorCashout', {
                value1: reportedValue,
                value: proposedValue
              }),
              'error',
              5000
            );
          } else {
            showToaster(response.data.messages[0].text, 'error', 5000);
          }
        } else if (response.data.messages[0].extraParam1 === '0') {
          showToaster(t('pages.bettingHistoryDetail.errorZeroCashout'), 'error',5000);
        } else {
          showToaster(
            t('pages.bettingHistoryDetail.errorCashout', {
              value1: cashoutValue,
              value: response.data.messages[0].extraParam1
            }),
            'error',5000
          );
        }
        setIsCashoutHandle(!isCashoutHandle);
      }
    } catch (error) {
      setIsCashoutHandle(!isCashoutHandle);
      errorToaster(error);
    }
    setIsSubmitting(false);
  };

  const handleCashoutInputChange = (e) => {
    if (data?.accountType === 5100) return;
    const cashOutInputValue = Number(e.target.value);
    setCashout(e.target.value);
    setIsCashoutInvalid(
      cashOutInputValue > cashoutValue || cashOutInputValue < 0 || isNaN(cashOutInputValue)
    );
  };

  if (!data) {
    return null;
  }

  return (
    <div className="bettinghistory">
      <Modal show={isSubmitting} onHide={() => setIsSubmitting(true)} />
      <Row className="pt-2 align-items-start">
        <Col xs={1}>
          <GoBack />
        </Col>
        <Col xs={9}>{titles}</Col>
      </Row>
      <hr className="my-2 text-color-white" />
      <div className="text-color-white-muted px-2">
        <div className="mb-1">
          <BettingHistoryTitle
            betSlipStatus={data?.betSlipStatus}
            count={
              data?.outcomeMap?.primaryBets
                ? Object.keys(data.outcomeMap.primaryBets).filter(
                    (k) => !data.outcomeMap.primaryBets[k].isBankoBet
                  ).length
                : (data?.matches?.length ?? 0)
            }
            betSlipType={data?.betSlipType}
            combinations={data?.combinations}
            numberOfBets={data?.numberOfBets}
          />
        </div>
        <div className="d-flex align-items-center justify-content-between mb-1">
          <div>
            {data?.accountType === 5100
              ? `${t('bonus')} ${t('pages.bettingHistoryDetail.details.stake')}`
              : t('pages.bettingHistoryDetail.details.stake')}
          </div>
          <div>
            {ConvertDisplayNumber(data?.totalStake)}
            {' ' + currencyCode}
          </div>
        </div>
        <div className="d-flex align-items-center justify-content-between mb-1">
          <div>
            {t(
              `pages.bettingHistoryDetail.details.${
                data?.betSlipStatus == EnumBetslipStatus.LOST ? 'maxwinnings' : 'winnings'
              }`
            )}
          </div>
          <div>
            <span>
              {data?.betSlipStatus == EnumBetslipStatus.LOST
                ? ConvertDisplayNumber(maxWinning)
                : ConvertDisplayNumber(data?.winning) || '0.00'}
              &nbsp;{currencyCode}
            </span>
            <button className="btn p-0" onClick={() => setShowBetDetails(!showBetDetails)}>
              <img
                height={30}
                width={30}
                className="icon"
                style={{
                  transform: `${!showBetDetails ? 'rotate(90deg)' : 'rotate(-90deg)'}`,
                  color: 'rgba(0, 0, 0, 0.7)'
                }}
                src={`${AWS_BUCKET_URL}/Icons/114.png`}
                alt={'expand stake area'}
              />
            </button>
          </div>
        </div>
        <Collapse in={showBetDetails}>
          <div className="fw-bold">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>{t('pages.bettingHistoryDetail.details.maxwinnings')}&#58;</div>
              <div>{ConvertDisplayNumber(maxWinning)}</div>
            </div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>{t('pages.bettingHistoryDetail.details.bettingslip')}&#58;</div>
              <div>{data?.systemSlipId}</div>
            </div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>{t('pages.bettingHistoryDetail.details.betTime')}&#58;</div>
              <div>
                {gameDay(data?.placeDate)}, {gameTime(data?.placeDate)}
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <div>{t('pages.bettingHistoryDetail.details.evaluationTime')}&#58;</div>
              <div>
                {data?.betSlipStatus == EnumBetslipStatus.WON &&
                  gameDay(data?.evaluationDate) + ', ' + gameTime(data?.evaluationDate)}
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between mb-2 ps-4 fw-normal small">
              <div>{t('pages.bettingHistoryDetail.details.stakePerBet')}&#58;</div>
              <div>
                {ConvertDisplayNumber(data?.stakePerBet)} {currencyCode} (TAX {ConvertDisplayNumber(data?.tax)}{' '}
                {currencyCode})
              </div>
            </div>
            <div className="d-flex align-items-center justify-content-between ps-4 fw-normal small">
              <div>{t('pages.bettingHistoryDetail.details.numberOfBets')}&#58;</div>
              <div>{data?.numberOfBets}</div>
            </div>
          </div>
        </Collapse>
      </div>
      {userLimits.isCashout && cashoutValue > 0 && data.accountType === 5000 && (
        <div id="deposit" className="mt-3 px-2">
          <Button
            onClick={() => setShowCashoutForm(!showCashoutForm)}
            variant="success"
            className="w-100 d-flex align-items-center justify-content-center">
            {t('pages.bettingHistoryDetail.cashout')} {cashoutValue}
            <span className="ms-2">
              {showCashoutForm ? (
                <img width="20" height="20" src={`${AWS_BUCKET_URL}/Icons/138.png`} />
              ) : (
                <img width="20" height="20" src={`${AWS_BUCKET_URL}/Icons/137.png`} />
              )}
            </span>
          </Button>
          <Collapse in={showCashoutForm}>
            <Form onSubmit={handleCashoutFormSubmit}>
              <div className="row">
                <div className="col mt-3">
                  <Form.Group controlId="formBasicEmail">
                    <Form.Control
                      type="number"
                      value={cashout}
                      placeholder={t('pages.bettingHistoryDetail.cashoutPlaceholder')}
                      onChange={handleCashoutInputChange}
                      isInvalid={isCashoutInvalid}
                      disabled={data?.accountType === 5100}
                    />
                    {isCashoutInvalid ? (
                      <Form.Control.Feedback className="d-block" type="invalid">
                        {t('pages.bettingHistoryDetail.errorText')}
                      </Form.Control.Feedback>
                    ) : (
                      <Form.Text className="text-muted">
                        {t('pages.bettingHistoryDetail.infoText')} {cashoutValue}
                      </Form.Text>
                    )}
                  </Form.Group>
                </div>
                <div className="col-auto mt-3">
                  <FetchButton className="w-100" type="submit" fetching={isSubmitting}>
                    {t(
                      `pages.bettingHistoryDetail.${
                        Number(cashout) === Number(cashoutValue) ? 'full' : 'partial'
                      }Cashout`
                    )}{' '}
                  </FetchButton>
                </div>
              </div>
            </Form>
          </Collapse>
        </div>
      )}
             {canRebet && (
         <div className="mt-3 px-2">
           <Button
             onClick={handleRebet}
             variant="primary"
             className="w-100 d-flex align-items-center justify-content-center"
             disabled={isRebetting}>
             <img 
               width="20" 
               height="20" 
               src={`${AWS_BUCKET_URL}/Icons/137.png`} 
               className="me-2"
               alt="re-bet"
             />
             {isRebetting ? t('loading') : t('rebetmatches', { count: availableMatchesForRebet.length })}
           </Button>
         </div>
       )}
             {!canRebet && data?.matches && data.matches.length > 0 && (
         <div className="mt-3 px-2">
           <div className="text-center text-muted">
             <small>{t('pages.bettingHistoryDetail.rebetNotAvailable')}</small>
           </div>
         </div>
       )}
      {(() => {
        const displayItems = getBetSlipDisplayItems(data);
        return displayItems.map((item, index) => {
          if (item.type === 'custom') {
            return (
              <div className="px-2" key={'custom-' + index}>
                <div className="mt10"></div>
                <div className="MatchCardListWrapper">
                  <BetMatchCard
                    match={item.baseMatch || item.event}
                    customOutcomes={item.outcomeDetails}
                    customCombinedOdds={item.customOutcome?.odds}
                    customOutcomeStatus={item.outcomeStatus}
                  />
                </div>
              </div>
            );
          }
          return (
            <div className="px-2" key={index}>
              <div className="mt10"></div>
              <div className="MatchCardListWrapper">
                <BetMatchCard key={'BetMatchCard' + index} match={item.match} />
              </div>
            </div>
          );
        });
      })()}
    </div>
  );
}
export default BettingHistoryDetail;
